import React, {
  createContext,
  useContext,
  useState,
  FC,
  ReactNode,
} from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'components/common';
import LucideIcon from 'components/common/LucideIcon';
import { t } from 'i18next';

interface DialogContextType {
  delete: (
    title: string,
    subTitle: string,
    textCheckbox?: string,
  ) => Promise<{ confirmed: boolean; checkboxValue: boolean }>;
  success: (title: string, subTitle: string) => Promise<boolean>;
  warning: (title: string, subTitle: string) => Promise<boolean>;
  custom: (component: ReactNode) => void;
  close: () => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

interface DialogProviderProps {
  children: ReactNode;
}

export const DialogProvider: FC<DialogProviderProps> = ({ children }) => {
  const [showDelete, setShowDelete] = useState(false);
  const [checkboxValue, setcheckboxValue] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<
    | ((
        value: boolean | { confirmed: boolean; checkboxValue: boolean },
      ) => void)
    | null
  >(null);
  const [customComponent, setCustomComponent] = useState<ReactNode | null>(
    null,
  );
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [dialogSubTitle, setDialogSubTitle] = useState<string>('');
  const [textCheckbox, setTextCheckbox] = useState<string | null>('');

  const deleteDialog = (
    title: string,
    subTitle: string,
    textCheckbox?: string,
  ): Promise<{ confirmed: boolean; checkboxValue: boolean }> => {
    setDialogTitle(title);
    setDialogSubTitle(subTitle);
    setShowDelete(true);
    setTextCheckbox(textCheckbox as string | null);
    return new Promise((resolve) => setResolvePromise(() => resolve));
  };

  const successDialog = (title: string, subTitle: string): Promise<boolean> => {
    setDialogTitle(title);
    setDialogSubTitle(subTitle);
    setShowSuccess(true);
    return new Promise((resolve) => setResolvePromise(() => resolve));
  };

  const warningDialog = (title: string, subTitle: string): Promise<boolean> => {
    setDialogTitle(title);
    setDialogSubTitle(subTitle);
    setShowWarning(true);
    return new Promise((resolve) => setResolvePromise(() => resolve));
  };

  const customDialog = (component: ReactNode) => {
    setCustomComponent(component);
    setShowCustom(true);
  };

  const handleDeleteConfirm = () => {
    if (resolvePromise)
      resolvePromise(textCheckbox ? { confirmed: true, checkboxValue } : true);
    setcheckboxValue(false);
    setShowDelete(false);
  };

  const handleDeleteCancel = () => {
    if (resolvePromise) resolvePromise(false);
    setShowDelete(false);
  };

  const handleSuccessConfirm = () => {
    if (resolvePromise) resolvePromise(true);
    setShowSuccess(false);
  };

  const handleSuccessCancel = () => {
    if (resolvePromise) resolvePromise(false);
    setShowSuccess(false);
  };

  const handleWarningConfirm = () => {
    if (resolvePromise) resolvePromise(true);
    setShowWarning(false);
  };

  const handleWarningCancel = () => {
    if (resolvePromise) resolvePromise(false);
    setShowWarning(false);
  };

  const handleCustomClose = () => {
    setCustomComponent(null);
    setShowCustom(false);
  };

  const closeDialog = () => {
    setShowCustom(false);
  };

  return (
    <DialogContext.Provider
      value={{
        delete: deleteDialog,
        success: successDialog,
        warning: warningDialog,
        custom: customDialog,
        close: closeDialog,
      }}
    >
      {children}

      {/* Delete dialog */}
      <Dialog
        visible={showDelete}
        onHide={handleDeleteCancel}
        dismissableMask={true}
        content={() => (
          <div
            className={`minthr-modal modal-content ${textCheckbox && 'minthr-w-100'} `}
          >
            <button
              onClick={handleDeleteCancel}
              className="minthr-modal-close"
              type="button"
              data-bs-dismiss="modal"
            >
              <img src="/icons/close.svg" alt="" />
            </button>
            <div className="minthr-text-align-center ">
              <img
                className="minthr-modal-icon minthr-mt-4 minthr-mb-6"
                src="/icons/error-icon.svg"
                alt="Error"
              />
              <h3 className="minthr-mb-2 minthr-font-weight-700">
                {t(dialogTitle)}
              </h3>
              <div className="minthr-color-gray">{t(dialogSubTitle)}</div>

              {textCheckbox && (
                <div className="row g-0 minthr-mb-4">
                  <div className="col-sm-12">
                    <div className="minthr-chackbox">
                      <input
                        type="checkbox"
                        name="replace"
                        id="replace"
                        className="minthr-form-control"
                        checked={checkboxValue}
                        onChange={(e) => setcheckboxValue(e.target.checked)}
                      />
                      <label className="minthr-form-label" htmlFor="replace">
                        {t(textCheckbox)}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-mt-6">
                <Button
                  className="w-100 minthr-btn minthr-btn-outline minthr-btn-danger minthr-mr-1"
                  onClick={handleDeleteCancel}
                >
                  {t('btn.cancel')}
                </Button>
                <Button
                  className="w-100 minthr-btn minthr-btn-danger minthr-ml-1"
                  onClick={handleDeleteConfirm}
                >
                  {t('Form.Button.Delete')}
                </Button>
              </div>
            </div>
          </div>
        )}
      />

      {/* Success dialog */}
      <Dialog
        visible={showSuccess}
        onHide={handleSuccessCancel}
        dismissableMask={true}
        content={() => (
          <div className="minthr-modal modal-content">
            <button
              onClick={handleSuccessCancel}
              className="minthr-modal-close"
              type="button"
              data-bs-dismiss="modal"
            >
              <img src="/icons/close.svg" alt="Close" />
            </button>
            <div className="minthr-text-align-center">
              <img
                className="minthr-modal-icon minthr-mt-4 minthr-mb-6"
                src="/icons/success-icon.svg"
                alt="Success"
              />
              <h3 className="minthr-mb-2 minthr-font-weight-700">
                {t(dialogTitle)}
              </h3>
              <div className="minthr-color-gray">{t(dialogSubTitle)}</div>
            </div>
            <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-mt-6">
              <Button
                className="w-100 minthr-btn minthr-btn-outline minthr-btn-success minthr-mr-1"
                onClick={handleSuccessCancel}
              >
                {t('btn.cancel')}
              </Button>
              <Button
                className="w-100 minthr-btn minthr-btn-success minthr-ml-1"
                onClick={handleSuccessConfirm}
              >
                {t('Btn.Validate')}
              </Button>
            </div>
          </div>
        )}
      />

      {/* Warning dialog */}
      <Dialog
        visible={showWarning}
        onHide={handleWarningCancel}
        dismissableMask={true}
        content={() => (
          <div className="minthr-modal modal-content">
            <button
              onClick={handleWarningCancel}
              className="minthr-modal-close"
              type="button"
              data-bs-dismiss="modal"
            >
              <img src="/icons/close.svg" alt="" />
            </button>
            <div className="minthr-text-align-center">
              <LucideIcon
                name="TriangleAlert"
                size="xxl"
                color="#F5A623"
                className="minthr-mt-4 minthr-mb-6"
              />
              <h3 className="minthr-mb-2 minthr-font-weight-700">
                {t(dialogTitle)}
              </h3>
              <div className="minthr-color-gray">{t(dialogSubTitle)}</div>
            </div>
            <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-mt-6">
              <Button
                className="w-100 minthr-btn minthr-btn-outline minthr-btn-primary minthr-mr-1"
                onClick={handleWarningCancel}
              >
                {t('btn.cancel')}
              </Button>
              <Button
                className="w-100 minthr-btn minthr-btn-primary minthr-ml-1"
                onClick={handleWarningConfirm}
              >
                {t('Btn.Validate')}
              </Button>
            </div>
          </div>
        )}
      />

      {/* Custom Component Dialog */}
      <Dialog
        dismissableMask={true}
        visible={showCustom}
        onHide={handleCustomClose}
        content={customComponent}
      />
    </DialogContext.Provider>
  );
};
